import { DiffType, GetCssClassNamesOption } from '@ardoq/data-model';
import { GroupType } from '@ardoq/api-types';
import { componentInterface } from '@ardoq/component-interface';
import { componentOperations } from '@ardoq/core';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { GraphItem, GraphItemMetadata } from './GraphItem';
import SparkMD5 from 'spark-md5';
import { referenceInterface } from '@ardoq/reference-interface';
import { ItemLabels } from '../types';
import {
  componentHasURLFields,
  getGroupTypeCss,
  getWorkspaceGroupCss,
} from './utils';

export class GraphGroup extends GraphItem {
  static create(
    modelId: string,
    label: string,
    type: GroupType,
    subLabels?: string[],
    id?: string
  ) {
    return new GraphGroup(modelId, label, type, subLabels, id);
  }
  readonly subLabel?: string;
  readonly type: GroupType;
  protected itemLabels: ItemLabels | undefined;

  protected constructor(
    modelId: string,
    label: string,
    type: GroupType,
    subLabels: string[] = [],
    id?: string
  ) {
    const graphItemMetadata: GraphItemMetadata = {
      isComponent: componentInterface.isComponent(modelId),
      isReference: referenceInterface.isReference(modelId),
      isGroup: true,
    };
    super(modelId, graphItemMetadata, id);
    this.type = type;
    this.subLabel = subLabels.join(', ');
    this.label = label;
    this.hash = SparkMD5.hash(this.getRawHash());
  }

  override getVisualDiffType(): DiffType {
    if (!this.isComponent()) return DiffType.NONE;
    const component = componentInterface.get(this.modelId);
    return component
      ? componentOperations.getVisualDiffType(component)
      : DiffType.NONE;
  }
  override hasURLFields() {
    if (this.isComponent()) {
      return componentHasURLFields(this.modelId);
    }
    return false;
  }
  override getCSS(options: GetCssClassNamesOption) {
    if (this.isComponent()) {
      return componentInterface.getCssClassNames(this.modelId, options) ?? '';
    }

    if (this.type === GroupType.WORKSPACE) {
      return getWorkspaceGroupCss(
        workspaceInterface.getCssClassNames(this.modelId)
      );
    }

    return getGroupTypeCss(this.type);
  }
  override getItemLabels(): ItemLabels | undefined {
    return this.itemLabels;
  }
  override setItemLabels(itemLabels: ItemLabels): void {
    this.itemLabels = itemLabels;
  }

  getRepresentationData() {
    if (this.isComponent()) {
      return componentInterface.getRepresentationData(this.modelId) ?? null;
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  getImage() {
    return '';
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  getShape() {
    return '';
  }

  protected getRawHash() {
    return `${super.getRawHash()}${this.subLabel}${this.getRepresentationData()?.value ?? ''}`;
  }
}
